import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { inboxKeys } from "./queryKeys";
import { getParticipantDetails, getParticipants } from "./api";
import { getChannel } from "@/components/inbox/utils";
import type {
	ChannelType,
	InboxConversation,
	InboxParticipant,
	MatchedCandidate,
	ParticipantDetails,
} from "@/types/inbox";

const PARTICIPANTS_CACHE_KEY = "inbox_participants_cache_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CachedParticipants {
	participants: InboxParticipant[];
	updatedAt: number;
}

function readParticipantsCache(conversationId: string): CachedParticipants | null {
	try {
		const stored = localStorage.getItem(PARTICIPANTS_CACHE_KEY);
		if (!stored) return null;
		const entry = JSON.parse(stored)[conversationId];
		if (!entry || Date.now() - (entry.updatedAt || 0) > CACHE_TTL_MS) return null;
		return entry;
	} catch {
		return null;
	}
}

function writeParticipantsCache(conversationId: string, participants: InboxParticipant[]): void {
	try {
		const stored = localStorage.getItem(PARTICIPANTS_CACHE_KEY);
		const cache = stored ? JSON.parse(stored) : {};
		cache[conversationId] = { participants, updatedAt: Date.now() };
		localStorage.setItem(PARTICIPANTS_CACHE_KEY, JSON.stringify(cache));
	} catch {
		// Quota/parse failures just mean a refetch next session.
	}
}

/**
 * Participants of a conversation. Email conversations already carry their
 * participants inline on the list payload; chats need a lookup, cached for
 * 24h in memory and localStorage (warm across reloads).
 */
export function useParticipants(conversation: InboxConversation | undefined) {
	const conversationId = conversation?.id;
	const inlineParticipants =
		getChannel(conversation) === "email" && conversation?.participants?.length
			? conversation.participants
			: undefined;

	return useQuery<InboxParticipant[]>({
		queryKey: inboxKeys.participants(conversationId || "none"),
		enabled: !!conversationId && !inlineParticipants,
		staleTime: CACHE_TTL_MS,
		gcTime: CACHE_TTL_MS,
		// Seed from inline data (email) or the persisted cache (chats) so a
		// still-fresh cache entry serves without refetching. Function form:
		// only evaluated when the query first mounts.
		initialData: () =>
			inlineParticipants ??
			(conversationId ? readParticipantsCache(conversationId)?.participants : undefined),
		initialDataUpdatedAt: () =>
			inlineParticipants
				? Date.now()
				: conversationId
					? readParticipantsCache(conversationId)?.updatedAt
					: undefined,
		queryFn: async () => {
			const participants = await getParticipants(conversationId!);
			writeParticipantsCache(conversationId!, participants);
			return participants;
		},
	});
}

/** Detailed profile (name, picture, LinkedIn URL, candidate link key) for one participant. */
export function useParticipantDetails(participantId: string | undefined) {
	return useQuery<ParticipantDetails>({
		queryKey: inboxKeys.participantDetails(participantId || "none"),
		enabled: !!participantId,
		staleTime: CACHE_TTL_MS,
		gcTime: CACHE_TTL_MS,
		queryFn: () => getParticipantDetails(participantId!),
	});
}

const CANDIDATE_COLUMNS = "id, name, email, function, linkedin";

async function findCandidateByLinkKey(
	linkKey: string,
	channel: ChannelType
): Promise<MatchedCandidate | null> {
	if (channel === "email") {
		const { data, error } = await supabase
			.from("candidates")
			.select(CANDIDATE_COLUMNS)
			.eq("email", linkKey)
			.limit(1);
		if (error) return null;
		return (data?.[0] as MatchedCandidate) || null;
	}

	if (channel === "linkedin") {
		// Try increasingly loose patterns against the stored profile URL.
		const patterns = [`%/${linkKey}%`, `%/${linkKey}/%`, `%${linkKey}%`];
		for (const pattern of patterns) {
			const { data, error } = await supabase
				.from("candidates")
				.select(CANDIDATE_COLUMNS)
				.ilike("linkedin", pattern)
				.limit(1);
			if (!error && data && data.length > 0) return data[0] as MatchedCandidate;
		}
	}
	return null;
}

/**
 * CRM candidate matched to a conversation counterpart — by email for email
 * conversations, by LinkedIn identifier for LinkedIn chats.
 */
export function useMatchedCandidate(
	channel: ChannelType,
	linkKey: string | null | undefined
) {
	const normalizedKey = linkKey?.trim() || "";
	return useQuery<MatchedCandidate | null>({
		queryKey: inboxKeys.matchedCandidate(channel, normalizedKey || "none"),
		enabled: !!normalizedKey && (channel === "email" || channel === "linkedin"),
		staleTime: CACHE_TTL_MS,
		queryFn: () => findCandidateByLinkKey(normalizedKey, channel),
	});
}
